import { create } from 'zustand';
import type { Receipt } from '@nako/types';
import { supabase } from '@/lib/supabase';
import { db, hashBlob, type QueuedReceipt } from '@/lib/db';

interface ReceiptsState {
  receipts: Receipt[];
  queued: QueuedReceipt[];
  loading: boolean;
  isFlushing: boolean;

  refresh: (businessId: string) => Promise<void>;
  enqueue: (businessId: string, image: Blob) => Promise<QueuedReceipt>;
  flushQueue: () => Promise<void>;
}

export const useReceiptsStore = create<ReceiptsState>((set, get) => ({
  receipts: [],
  queued: [],
  loading: false,
  isFlushing: false,

  refresh: async (businessId) => {
    set({ loading: true });
    const { data, error } = await supabase
      .from('receipts')
      .select('*')
      .eq('business_id', businessId)
      .order('uploaded_at', { ascending: false })
      .limit(100);
    if (error) {
      console.error('refresh receipts', error);
    }
    set({ receipts: data ?? [], loading: false });

    const queued = await db.queuedReceipts.toArray();
    set({ queued });
  },

  enqueue: async (businessId, image) => {
    const local_id = crypto.randomUUID();
    const image_hash = await hashBlob(image);
    const captured_at = new Date().toISOString();
    const item: QueuedReceipt = {
      local_id,
      business_id: businessId,
      image_blob: image,
      image_hash,
      captured_at,
      sync_status: 'pending',
      sync_attempts: 0,
    };
    await db.queuedReceipts.add(item);
    set({ queued: [...get().queued, item] });
    return item;
  },

  flushQueue: async () => {
    // Concurrency lock: a manual tap and a network-reconnect handler can both
    // call flushQueue. Without this they double-upload the same image and fire
    // duplicate OCR jobs (Round-2 finding #3).
    if (get().isFlushing) return;
    set({ isFlushing: true });
    try {
      const MAX_RETRIES = 5;
      // Include 'syncing': an item left in 'syncing' by a crashed/interrupted
      // run would otherwise be stranded forever. The lock above guarantees no
      // other run owns it, so it's safe to re-pick up here.
      const pending = await db.queuedReceipts
        .where('sync_status')
        .anyOf('pending', 'failed', 'syncing')
        .toArray();
      const retryable = pending.filter((item) => item.sync_attempts < MAX_RETRIES);
      for (const item of retryable) {
        await db.queuedReceipts.update(item.local_id, { sync_status: 'syncing' });

        try {
          const path = `${item.business_id}/${item.image_hash}.jpg`;
          const { error: upErr } = await supabase.storage
            .from('receipts')
            .upload(path, item.image_blob, { upsert: true, contentType: 'image/jpeg' });
          if (upErr) throw upErr;

          // Receipts bucket is PRIVATE (0019). Store the object path and mint a
          // short-lived signed URL only for the OCR Edge Function to fetch.
          const { data: signed, error: signErr } = await supabase.storage
            .from('receipts')
            .createSignedUrl(path, 600);
          if (signErr || !signed) throw signErr ?? new Error('could not sign receipt URL');

          const { data: row, error: insErr } = await supabase
            .from('receipts')
            .insert({
              business_id: item.business_id,
              image_url: path,
              image_hash: item.image_hash,
              ocr_status: 'pending',
            })
            .select()
            .single();
          // A (business_id, image_hash) unique violation means this receipt was
          // already synced by an earlier/overlapping run — treat as success.
          if (insErr) {
            if ((insErr as { code?: string }).code === '23505') {
              await db.queuedReceipts.delete(item.local_id);
              continue;
            }
            throw insErr;
          }

          // Trigger Edge Function (fire-and-forget — UI polls receipt status).
          await supabase.functions.invoke('scan-receipt', {
            body: {
              receipt_id: row.id,
              image_url: signed.signedUrl,
              business_id: item.business_id,
            },
          });

          await db.queuedReceipts.delete(item.local_id);
        } catch (e) {
          const msg = e instanceof Error ? e.message : String(e);
          await db.queuedReceipts.update(item.local_id, {
            sync_status: 'failed',
            sync_attempts: item.sync_attempts + 1,
            last_error: msg,
          });
        }
      }
      const queued = await db.queuedReceipts.toArray();
      set({ queued });
    } finally {
      set({ isFlushing: false });
    }
  },
}));
